import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '@/features/game/store/gameStore';
import { Button } from '@/components/ui/button';
import { 
  Trophy, Home, RotateCcw, Star, 
  Crown, Medal, Sparkles
} from 'lucide-react';
import confetti from 'canvas-confetti';

export const ResultsPage = () => {
  const navigate = useNavigate();
  const {
    team1,
    team2,
    questionCells,
    reset,
    resetTeamsOnly,
  } = useGameStore();

  // Redirect if no game data
  useEffect(() => {
    if (!team1 || !team2) {
      navigate('/player');
    }
  }, [team1, team2, navigate]);

  // Fire confetti on mount
  useEffect(() => {
    if (team1 && team2) {
      // Determine winner and fire appropriate confetti
      const winnerColor = team1.score > team2.score ? '#E53935' : '#1B5E20';
      
      confetti({
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: [winnerColor, '#FFD700', '#FFA500'],
      });
    }
  }, [team1, team2]);

  if (!team1 || !team2) {
    return null;
  }

  // Determine winner
  const isDraw = team1.score === team2.score;
  const winner = isDraw ? null : (team1.score > team2.score ? team1 : team2);

  // Calculate stats
  const answeredQuestions = questionCells.filter((c) => c.isAnswered).length;
  const totalQuestions = questionCells.length;
  const team1Answered = questionCells.filter((c) => c.answeredBy === 'team1').length;
  const team2Answered = questionCells.filter((c) => c.answeredBy === 'team2').length;

  const handlePlayAgain = () => {
    resetTeamsOnly();
    navigate('/player/team-setup');
  };

  const handleNewGame = () => {
    reset();
    navigate('/player/game-settings');
  };

  const handleExitToHome = () => {
    reset();
    navigate('/player');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 p-4 flex flex-col items-center justify-center">
      {/* Winner Announcement */}
      <div className="text-center mb-8">
        {isDraw ? (
          <>
            <div className="text-6xl mb-4">🤝</div>
            <h1 className="text-4xl md:text-6xl font-bold text-yellow-400 mb-2">
              تعادل!
            </h1>
            <p className="text-xl text-gray-300">كلا الفريقين متعادلان</p>
          </>
        ) : (
          <>
            <div className="relative inline-block">
              <Crown className="w-20 h-20 text-yellow-400 mx-auto mb-4 animate-bounce" />
              <Sparkles className="absolute top-0 -right-4 w-8 h-8 text-yellow-300 animate-pulse" />
              <Sparkles className="absolute top-0 -left-4 w-8 h-8 text-yellow-300 animate-pulse" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-yellow-400 mb-2">
              الفائز!
            </h1>
            <h2 className={`text-5xl md:text-7xl font-bold mb-4 ${
              winner === team1 ? 'text-red-500' : 'text-green-500'
            }`}>
              {winner?.name}
            </h2>
            <div className="flex items-center justify-center gap-2 text-yellow-300">
              <Trophy className="w-8 h-8" />
              <span className="text-4xl font-bold">{winner?.score} نقطة</span>
            </div>
          </>
        )}
      </div>

      {/* Score Cards */}
      <div className="grid md:grid-cols-2 gap-6 max-w-4xl w-full mb-8">
        {/* Team 1 Card */}
        <div className={`rounded-3xl p-6 ${
          winner === team1 
            ? 'bg-gradient-to-br from-red-600 to-red-700 ring-4 ring-yellow-400' 
            : 'bg-gradient-to-br from-red-800/50 to-red-900/50'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {winner === team1 && <Medal className="w-10 h-10 text-yellow-400" />}
              <h3 className="text-2xl font-bold text-white">{team1.name}</h3>
            </div>
            {winner === team1 && (
              <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                الفائز
              </span>
            )}
          </div>
          
          <div className="text-center py-6">
            <p className="text-6xl font-bold text-white">{team1.score}</p>
            <p className="text-red-200 mt-2">نقطة</p>
          </div>

          <div className="bg-white/10 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-red-100">
              <span>الأسئلة المجابة:</span>
              <span className="font-bold">{team1Answered}</span>
            </div>
            <div className="flex justify-between text-red-100">
              <span>عدد الأعضاء:</span>
              <span className="font-bold">{team1.memberCount}</span>
            </div>
          </div>
        </div>

        {/* Team 2 Card */}
        <div className={`rounded-3xl p-6 ${
          winner === team2 
            ? 'bg-gradient-to-br from-green-600 to-green-700 ring-4 ring-yellow-400' 
            : 'bg-gradient-to-br from-green-800/50 to-green-900/50'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {winner === team2 && <Medal className="w-10 h-10 text-yellow-400" />}
              <h3 className="text-2xl font-bold text-white">{team2.name}</h3>
            </div>
            {winner === team2 && (
              <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold">
                الفائز
              </span>
            )}
          </div>
          
          <div className="text-center py-6">
            <p className="text-6xl font-bold text-white">{team2.score}</p>
            <p className="text-green-200 mt-2">نقطة</p>
          </div>

          <div className="bg-white/10 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-green-100">
              <span>الأسئلة المجابة:</span>
              <span className="font-bold">{team2Answered}</span>
            </div>
            <div className="flex justify-between text-green-100">
              <span>عدد الأعضاء:</span>
              <span className="font-bold">{team2.memberCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Game Stats */}
      <div className="bg-white/10 rounded-2xl p-6 max-w-md w-full mb-8">
        <h3 className="text-xl font-bold text-white text-center mb-4">إحصائيات اللعبة</h3>
        <div className="space-y-3">
          <div className="flex justify-between text-gray-300">
            <span>إجمالي الأسئلة:</span>
            <span className="font-bold text-white">{totalQuestions}</span>
          </div>
          <div className="flex justify-between text-gray-300">
            <span>الأسئلة المجابة:</span>
            <span className="font-bold text-white">{answeredQuestions}</span>
          </div>
          <div className="flex justify-between text-gray-300">
            <span>نسبة الإكمال:</span>
            <span className="font-bold text-white">
              {Math.round((answeredQuestions / totalQuestions) * 100)}%
            </span>
          </div>
          <div className="flex justify-between text-gray-300">
            <span>مجموع النقاط:</span>
            <span className="font-bold text-yellow-400">{team1.score + team2.score}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col md:flex-row gap-4 w-full max-w-md">
        <Button
          onClick={handlePlayAgain}
          className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-6 text-lg"
        >
          <RotateCcw className="w-5 h-5 ml-2" />
          إعادة اللعب
        </Button>
        
        <Button
          onClick={handleNewGame}
          variant="outline"
          className="flex-1 border-2 border-white text-white hover:bg-white/10 font-bold py-6 text-lg"
        >
          <Star className="w-5 h-5 ml-2" />
          لعبة جديدة
        </Button>
      </div>

      <Button
        onClick={handleExitToHome}
        variant="ghost"
        className="text-gray-400 hover:text-white mt-4"
      >
        <Home className="w-5 h-5 ml-2" />
        العودة للرئيسية
      </Button>
    </div>
  );
};
